import { createAsyncThunk } from '@reduxjs/toolkit'
import { handleError, hasError } from '@repo/shared/api/helpers'
import { FilterTemplateDTO } from '@repo/shared/dtos/beneficiary'
import { SuccessResponseDTO } from '@repo/shared/dtos/generic'
import { DataStatus } from '@repo/ui/utils'

import { buildBeneficiaryFromDto } from '@/external/api/helpers'
import { CurrentFilterTemplate } from '@/store/beneficiary-list-filters/types'

import { AsyncThunkConfig, RejectValue } from '../actions'
import { Beneficiary } from '../beneficiary'

export const getBeneficiaries = createAsyncThunk<
  { beneficiaries: Beneficiary[]; meta: { maxPage: number; totalItems: number } },
  CurrentFilterTemplate,
  AsyncThunkConfig
>(
  'GET_BENEFICIARIES',
  async (currentFilterTemplate, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const scope = state.beneficiaryList.scope
    const { sortOption, pagination } = state.beneficiaryList

    const res = await extra.altApi.beneficiaries.search({
      filters: currentFilterTemplate.filters,
      organizationId,
      page: pagination.page,
      perPage: pagination.perPage,
      scope,
      sortOption,
    })

    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue({
      beneficiaries: res.beneficiaries.map(buildBeneficiaryFromDto),
      meta: res.meta,
    })
  },
)

export const getFilterTemplates = createAsyncThunk<FilterTemplateDTO[], void, AsyncThunkConfig>(
  'GET_FILTER_TEMPLATES',
  async (_, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const organizationId = getState().user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const res = await extra.altApi.filterTemplates.getAll({ organizationId })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const createFilterTemplate = createAsyncThunk<
  FilterTemplateDTO,
  Pick<FilterTemplateDTO, 'name' | 'filters'>,
  AsyncThunkConfig
>(
  'SAVE_FILTER_TEMPLATE',
  async (fields, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const caseWorkder = state.user.caseWorker
    if (!DataStatus.isFulfilled(caseWorkder)) return rejectWithValue(RejectValue.NoCaseWorker)

    const res = await extra.altApi.filterTemplates.createOne({
      organizationId,
      caseWorkerId: caseWorkder.id,
      fields,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const updateFilterTemplate = createAsyncThunk<
  FilterTemplateDTO,
  Pick<FilterTemplateDTO, 'name' | 'filters'>,
  AsyncThunkConfig
>(
  'UPDATE_FILTER_TEMPLATE',
  async (fields, { getState, extra, rejectWithValue, fulfillWithValue, dispatch }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const caseWorkder = state.user.caseWorker
    if (!DataStatus.isFulfilled(caseWorkder)) return rejectWithValue(RejectValue.NoCaseWorker)

    const filterTemplate = state.beneficiaryList.currentFilterTemplate
    if (!('id' in filterTemplate)) return rejectWithValue(RejectValue.NoUpdate)

    const res = await extra.altApi.filterTemplates.updateOne({
      organizationId,
      caseWorkerId: caseWorkder.id,
      filterTemplateId: filterTemplate.id,
      fields,
    })
    void dispatch(getFilterTemplates())
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const deleteFilterTemplate = createAsyncThunk<
  SuccessResponseDTO,
  FilterTemplateDTO['id'],
  AsyncThunkConfig
>(
  'DELETE_FILTER_TEMPLATE',
  async (filterTemplateId, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)
    const caseWorkder = state.user.caseWorker
    if (!DataStatus.isFulfilled(caseWorkder)) return rejectWithValue(RejectValue.NoCaseWorker)

    const res = await extra.altApi.filterTemplates.deleteOne({
      organizationId,
      caseWorkerId: caseWorkder.id,
      filterTemplateId,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)

export const exportBeneficiariesToCsv = createAsyncThunk<
  { csv: string },
  { columnIds: string[] },
  AsyncThunkConfig
>(
  'EXPORT_BENEFICIARIES_TO_CSV',
  async ({ columnIds }, { getState, extra, rejectWithValue, fulfillWithValue }) => {
    const state = getState()
    const organizationId = state.user.currentOrganizationId
    if (!organizationId) return rejectWithValue(RejectValue.NoOrganization)

    const { sortOption, currentFilterTemplate, scope } = state.beneficiaryList

    const res = await extra.altApi.beneficiaries.exportToCsv({
      filters: currentFilterTemplate.filters,
      organizationId,
      sortOption,
      columnIds,
      scope,
    })
    if (hasError(res)) return rejectWithValue(handleError(res))
    return fulfillWithValue(res)
  },
)
